/* Print-friendly flipbook layout. One A5-sized "page" per item, stacked vertically.
   Used by the backend /api/flipbooks/{slug}/pdf endpoint with headless Chrome.
   Visual styling matches the on-screen flipbook 1:1. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

// Page size constants — 5.83in x 8.27in (A5) in CSS px at 96dpi
const PAGE_W = 560;   // CSS px
const PAGE_H = 794;   // CSS px (≈ 1.418 ratio, A5)

const pageBoxStyle = {
  width: `${PAGE_W}px`,
  height: `${PAGE_H}px`,
  background: "#FAF9F6",
  backgroundImage:
    "radial-gradient(ellipse at top left, rgba(201,162,39,0.08), transparent 60%), radial-gradient(ellipse at bottom right, rgba(139,0,0,0.05), transparent 60%)",
  position: "relative",
  overflow: "hidden",
  pageBreakAfter: "always",
  breakAfter: "page",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
};

export function CoverP({ page, fb }) {
  return (
    <div style={{ ...pageBoxStyle, padding: 0 }}>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        {page.image_url ? (
          <img
            src={imgSrc(page.image_url)} alt={fb.title}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(160deg, #0F172A, #0F172A, #2E9F3A)" }}/>
        )}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.85) 100%)",
        }}/>
        <div style={{
          position: "absolute", left: 36, right: 36, bottom: 50, color: "#FAF9F6",
          textShadow: "0 2px 12px rgba(0,0,0,0.6)",
        }}>
          <div style={{ fontSize: 11, letterSpacing: "0.32em", color: "#F39A1F", fontWeight: 700, marginBottom: 12 }}>
            ★ FAITHARAMA · A FLIPBOOK ★
          </div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 44, lineHeight: 1.05, margin: 0, fontWeight: 700,
          }}>{page.heading || fb.title}</h1>
          {fb.subtitle && (
            <p style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 18, fontStyle: "italic", marginTop: 12, opacity: 0.9,
            }}>{fb.subtitle}</p>
          )}
          <div style={{ marginTop: 20, fontSize: 11, letterSpacing: "0.24em", fontWeight: 700 }}>
            BY {(fb.author_name || "FAITHARAMA").toUpperCase()}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ImageTextP({ page }) {
  return (
    <div style={pageBoxStyle}>
      <div style={{
        width: "100%", height: "44%",
        background: "linear-gradient(135deg, #F4EFE6 0%, #EAE3D5 100%)",
        borderBottom: "3px solid #F39A1F",
        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
      }}>
        {page.image_url && (
          <img src={imgSrc(page.image_url)} alt={page.heading}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}/>
        )}
      </div>
      <div style={{ flex: 1, padding: "26px 30px", overflow: "hidden" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.28em", color: "#2E9F3A", fontWeight: 700 }}>
          ★ {page.heading} ★
        </div>
        <div
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 17, lineHeight: 1.55, color: "#0F172A", fontWeight: 500, marginTop: 12,
          }}
          dangerouslySetInnerHTML={{ __html: page.body }}
        />
      </div>
    </div>
  );
}

export function TextP({ page }) {
  return (
    <div style={pageBoxStyle}>
      <div style={{ flex: 1, padding: "44px 36px 30px", overflow: "hidden" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.32em", color: "#F39A1F", fontWeight: 700, marginBottom: 8 }}>
          ★ FAITHARAMA ★
        </div>
        <h2 style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 30, lineHeight: 1.05, margin: "0 0 22px",
          color: "#0F172A", fontWeight: 700, textTransform: "uppercase",
        }}>{page.heading}</h2>
        <div style={{ width: 60, height: 3, background: "#F39A1F", marginBottom: 22 }}/>
        <div
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 17, lineHeight: 1.6, color: "#0F172A", fontWeight: 500,
          }}
          dangerouslySetInnerHTML={{ __html: page.body }}
        />
      </div>
    </div>
  );
}

export function QuizP({ page }) {
  return (
    <div style={pageBoxStyle}>
      <div style={{ padding: "32px 28px", display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.32em", color: "#F39A1F", fontWeight: 700, marginBottom: 10 }}>
          ★ {page.heading} ★
        </div>
        <h2 style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 22, lineHeight: 1.2, margin: "8px 0 18px", color: "#0F172A", fontWeight: 700,
        }}>{page.body}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {(page.options || []).map((opt, i) => {
            const correct = i === page.correct;
            return (
              <div key={i} style={{
                padding: "10px 12px", borderRadius: 8,
                border: `2px solid ${correct ? "#22693C" : "#E5DDD3"}`,
                background: correct ? "#E8F5EC" : "#FFFFFF",
                color: correct ? "#22693C" : "#0F172A",
                fontWeight: 600, display: "flex", alignItems: "center", gap: 10,
              }}>
                <span style={{
                  width: 22, height: 22, borderRadius: "50%",
                  background: correct ? "#22693C" : "#F39A1F",
                  color: "#FFF", fontWeight: 700, fontSize: 11,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{String.fromCharCode(65 + i)}</span>
                <span style={{ flex: 1, fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 15 }}>
                  {opt}
                </span>
                {correct && <span style={{ color: "#22693C", fontWeight: 700 }}>✓</span>}
              </div>
            );
          })}
        </div>
        {page.explanation && (
          <div style={{
            marginTop: 16, padding: "12px 14px", borderRadius: 8,
            background: "#EAF6FC", border: "1px solid #F39A1F",
            fontSize: 13, color: "#0F172A", lineHeight: 1.5,
            fontFamily: "'Cormorant Garamond', Georgia, serif",
          }}>
            <strong style={{ color: "#22693C" }}>Answer: </strong>{page.explanation}
          </div>
        )}
      </div>
    </div>
  );
}

export function QuizResultP({ page }) {
  return (
    <div style={pageBoxStyle}>
      <div style={{ padding: "60px 36px", textAlign: "center", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.32em", color: "#F39A1F", fontWeight: 700, marginBottom: 16 }}>
          ★ {page.heading} ★
        </div>
        <div style={{
          width: 120, height: 120, borderRadius: "50%",
          background: "linear-gradient(135deg, #F39A1F, #E5C04F)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 24px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        }}>
          <div style={{ fontFamily: "Anton, Impact, sans-serif", fontSize: 42, color: "#FFF" }}>★</div>
        </div>
        <div
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 18, color: "#0F172A", lineHeight: 1.6, fontWeight: 500 }}
          dangerouslySetInnerHTML={{ __html: page.body }}
        />
      </div>
    </div>
  );
}

export const RENDER = {
  cover: CoverP,
  image_text: ImageTextP,
  text: TextP,
  quiz: QuizP,
  quiz_result: QuizResultP,
};

export default function FlipbookPrint() {
  const { slug } = useParams();
  const [fb, setFb] = useState(null);
  const [imagesReady, setImagesReady] = useState(false);

  useEffect(() => {
    api.get(`/flipbooks/${slug}`).then(r => setFb(r.data)).catch(() => {});
  }, [slug]);

  // After flipbook loads, wait until every <img> has settled before
  // signaling "ready" — the headless Chrome printer waits for this signal.
  useEffect(() => {
    if (!fb) return;
    let cancelled = false;
    (async () => {
      // Yield so React paints the DOM
      await new Promise(r => setTimeout(r, 100));
      const imgs = Array.from(document.images);
      await Promise.all(
        imgs.map(img =>
          img.complete ? Promise.resolve() :
          new Promise(res => {
            img.addEventListener("load", res, { once: true });
            img.addEventListener("error", res, { once: true });
          })
        )
      );
      // Tiny extra grace period for fonts/layout
      await new Promise(r => setTimeout(r, 500));
      if (!cancelled) setImagesReady(true);
    })();
    return () => { cancelled = true; };
  }, [fb]);

  if (!fb) return <div style={{ padding: 40 }}>Loading…</div>;

  return (
    <div
      data-testid="flipbook-print"
      data-print-ready={imagesReady ? "true" : "false"}
      style={{ background: "#FFFFFF", minHeight: "100vh" }}
    >
      <style>{`
        @page { size: 5.83in 8.27in; margin: 0; }
        @media print {
          body { margin: 0; padding: 0; background: #FFF; }
          [data-testid="flipbook-print"] > div { page-break-after: always; break-after: page; }
        }
        body { margin: 0; padding: 0; }
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;700&family=Anton&display=swap');
      `}</style>
      {fb.pages.map((p, i) => {
        const Comp = RENDER[p.kind] || TextP;
        return (
          <div key={i} style={{ display: "flex", justifyContent: "center", margin: 0 }}>
            <Comp page={p} fb={fb}/>
          </div>
        );
      })}
    </div>
  );
}
